/**
 * ConfirmDialog Component
 *
 * Replaces native confirm()/alert() with a styled glassmorphism dialog.
 */

import React from 'react';
import { Modal } from './Modal';
import { AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

const variantStyles = {
  danger: {
    icon: AlertTriangle,
    iconBg: 'bg-[#FF3B30]/12',
    iconColor: 'text-[#FF3B30]',
    buttonBg: 'bg-[#FF3B30] hover:bg-[#FF3B30]/80',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-[#FF9500]/12',
    iconColor: 'text-[#FF9500]',
    buttonBg: 'bg-[#FF9500] hover:bg-[#FF9500]/80',
  },
  info: {
    icon: Info,
    iconBg: 'bg-[#5C2D91]/8',
    iconColor: 'text-[#5C2D91]',
    buttonBg: 'bg-[#5C2D91] hover:bg-[#5C2D91]/80',
  },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  loading = false,
}) => {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('Confirm');
  const resolvedCancelLabel = cancelLabel ?? t('Cancel');
  const styles = variantStyles[variant];
  const Icon = styles.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <div className="text-center">
        <div className={`w-14 h-14 ${styles.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-7 h-7 ${styles.iconColor}`} />
        </div>
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{title}</h3>
        <p className="text-[var(--text-secondary)] text-sm mb-6">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-black/[0.04] border border-[var(--border-default)] rounded-xl text-[var(--text-secondary)] font-medium hover:bg-black/[0.08] transition-colors disabled:opacity-50"
          >
            {resolvedCancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 ${styles.buttonBg} text-white rounded-xl font-medium transition-colors disabled:opacity-50`}
          >
            {loading ? t('Processing...') : resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};
